/**
 * Caret / selection capture and restore for text-like controls (input, textarea, contenteditable).
 */

const INPUT_TYPES_WITHOUT_SELECTION = new Set([
   "button",
   "checkbox",
   "radio",
   "submit",
   "reset",
   "file",
   "image",
   "hidden",
   "range",
   "color",
]);

export type FocusTextSelection =
   | { kind: "input"; start: number; end: number }
   | { kind: "contenteditable"; start: number; end: number };

export function isTextLikeInput(el: Element): el is HTMLInputElement | HTMLTextAreaElement {
   if (el instanceof HTMLTextAreaElement) {
      return true;
   }
   if (el instanceof HTMLInputElement) {
      return !INPUT_TYPES_WITHOUT_SELECTION.has(el.type);
   }
   return false;
}

export function isContentEditableSurface(el: Element): el is HTMLElement {
   if (!(el instanceof HTMLElement)) {
      return false;
   }
   // Prefer the live IDL flag; fall back to the attribute for environments (e.g. happy-dom)
   // that do not mirror contenteditable="true" onto isContentEditable.
   if (el.isContentEditable) {
      return true;
   }
   const attr = el.getAttribute("contenteditable");
   return attr === "true" || attr === "";
}

function contentEditableOffsets(el: HTMLElement): { start: number; end: number } | null {
   const selection = window.getSelection();
   if (!selection || selection.rangeCount === 0) {
      return null;
   }

   const range = selection.getRangeAt(0);
   if (!el.contains(range.commonAncestorContainer)) {
      return null;
   }

   const toOffset = (container: Node, offset: number): number => {
      const pre = document.createRange();
      pre.selectNodeContents(el);
      pre.setEnd(container, offset);
      return pre.toString().length;
   };

   return {
      start: toOffset(range.startContainer, range.startOffset),
      end: toOffset(range.endContainer, range.endOffset),
   };
}

export function captureSelection(el: Element): FocusTextSelection | null {
   if (isTextLikeInput(el)) {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      if (start == null || end == null) {
         return null;
      }
      return { kind: "input", start, end };
   }

   if (isContentEditableSurface(el)) {
      const offsets = contentEditableOffsets(el);
      if (!offsets) {
         return null;
      }
      return { kind: "contenteditable", start: offsets.start, end: offsets.end };
   }

   return null;
}

function walkTextNodes(root: Node): Text[] {
   const texts: Text[] = [];
   const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
   let node = walker.nextNode();
   while (node) {
      texts.push(node as Text);
      node = walker.nextNode();
   }
   return texts;
}

function setContentEditableSelection(el: HTMLElement, start: number, end: number): void {
   const texts = walkTextNodes(el);
   const total = texts.reduce((sum, t) => sum + (t.nodeValue?.length ?? 0), 0);
   const clampedStart = Math.max(0, Math.min(start, total));
   const clampedEnd = Math.max(0, Math.min(end, total));

   const pointAt = (offset: number): { node: Text; offset: number } | null => {
      let remaining = offset;
      for (const text of texts) {
         const length = text.nodeValue?.length ?? 0;
         if (remaining <= length) {
            return { node: text, offset: remaining };
         }
         remaining -= length;
      }
      if (texts.length === 0) {
         return null;
      }
      const last = texts[texts.length - 1]!;
      return { node: last, offset: last.nodeValue?.length ?? 0 };
   };

   const startPoint = pointAt(clampedStart);
   const endPoint = pointAt(clampedEnd);
   if (!startPoint || !endPoint) {
      return;
   }

   const range = document.createRange();
   range.setStart(startPoint.node, startPoint.offset);
   range.setEnd(endPoint.node, endPoint.offset);

   const selection = window.getSelection();
   selection?.removeAllRanges();
   selection?.addRange(range);
}

export function restoreSelection(el: HTMLElement, selection: FocusTextSelection | null): void {
   if (!selection) {
      return;
   }

   if (selection.kind === "input" && isTextLikeInput(el)) {
      const length = el.value.length;
      const start = Math.max(0, Math.min(selection.start, length));
      const end = Math.max(0, Math.min(selection.end, length));
      try {
         el.setSelectionRange(start, end);
      } catch {
         // Some input types reject setSelectionRange; focus-only is enough.
      }
      return;
   }

   if (selection.kind === "contenteditable" && isContentEditableSurface(el)) {
      try {
         setContentEditableSelection(el, selection.start, selection.end);
      } catch {
         // Best-effort: focus already applied; invalid ranges are ignored.
      }
   }
}
